import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient } from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import '@fontsource-variable/archivo/wdth.css';
import './styles.css';
import { App } from './App.jsx';
import { ToastProvider } from './state/toast.jsx';
import { TimerProvider } from './state/timer.jsx';
import { AuthProvider } from './state/auth.jsx';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 10 * 60_000,
      // 429 = Exxas pausiert (zu viele Anfragen): nach der verlangten Wartezeit einmal selbst neu versuchen
      retry: (count, err) => (err?.status === 429 ? count < 1 : err?.status >= 500 && err?.status !== 504 && count < 2),
      retryDelay: (count, err) => (err?.status === 429 ? Math.min((err.retryAfter ?? 30) * 1000 + 1000, 70_000) : Math.min(1000 * 2 ** count, 8000)),
      // Exxas-Aufrufe sparen: nicht automatisch neu laden beim Zurückwechseln oder nach Funkloch –
      // nur beim Ansehen (wenn älter als 10 min) oder über «Aktualisieren»
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      gcTime: 24 * 3_600_000, // so lange wie die Offline-Kopie, sonst wäre sie nach dem Neuladen leer
    },
    // Buchungen nicht stillschweigend pausieren, wenn das Gerät offline ist – das übernimmt die Outbox
    mutations: { networkMode: 'always' },
  },
});

/**
 * Offline-Kopie der zuletzt geladenen Daten (Auftrag, Zeit, Material, Stammdaten) auf dem Gerät, damit die App
 * auch im Funkloch nach einem Neustart weiter nutzbar ist. Liegt unter «bs.» und wird beim Abmelden gelöscht.
 * Suchergebnisse und Dateien werden nicht gespeichert.
 */
const PERSIST = new Set(['me', 'auftrag', 'leistungen', 'material', 'leistungsarten', 'kommentare', 'zeitplan', 'dateien', 'kunde', 'favoriten', 'meineStunden', 'checkliste', 'checklisten', 'plaene', 'plan', 'formulare', 'formularVorlage']);
let storage;
try { storage = window.localStorage; } catch { storage = undefined; }
const persister = createSyncStoragePersister({ storage, key: 'bs.offline-daten', throttleTime: 1000 });
const persistOptions = {
  persister,
  maxAge: 24 * 3_600_000,
  buster: 'v1',
  dehydrateOptions: {
    // Formulare aus Vorlagen («formularListe:…», «formular:…»): offline weiterarbeiten können
    shouldDehydrateQuery: (q) => q.state.status === 'success' && (PERSIST.has(q.queryKey[0]) || String(q.queryKey[0]).startsWith('formular')),
  },
};

// App-Hülle offline verfügbar machen (nur im Build; im Dev-Server stört ein Cache beim Entwickeln)
if (import.meta.env.PROD && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => navigator.serviceWorker.register('/sw.js').catch(() => {}));
}

createRoot(document.getElementById('root')).render(
  <StrictMode>
    <PersistQueryClientProvider client={queryClient} persistOptions={persistOptions}>
      <BrowserRouter>
        <ToastProvider>
          <AuthProvider>
            <TimerProvider>
              <App />
            </TimerProvider>
          </AuthProvider>
        </ToastProvider>
      </BrowserRouter>
    </PersistQueryClientProvider>
  </StrictMode>,
);
